import { useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { useMutation } from '@tanstack/react-query';
import * as service from '../services';
import { useDebounce } from '../../../../../hooks';
import { useAnaliseCoberturaStore, coverageInitialState } from '../AnaliseCobertura.store';
import { Filter, CategoryContent } from '../types';

export const useCoverageAnalysisCategory = () => {
  const { setCoverageAnalysisState, categoryCoverageParams, category } = useAnaliseCoberturaStore();

  const debounce = useDebounce(500);

  const categoryCoverageSuccess = (data: AxiosResponse) => {
    setCoverageAnalysisState('categoryCoverageData', data.data);
  };

  const mutation = useMutation({
    mutationFn: data => service.getCoverageAnalysisCategory(data),
    onSuccess: data => categoryCoverageSuccess(data),
  });

  const handleChangeCategoryCoveragePeriod = (value: number) => {
    const param = { ...categoryCoverageParams, diasAnalise: value };
    setCoverageAnalysisState('categoryCoverageParams', param);
    mutation.mutateAsync(param);
  };

  const handleChangeCategoryCoveragePagination = (value: number) => {
    const param = { ...categoryCoverageParams, page: value - 1 };
    setCoverageAnalysisState('categoryCoverageParams', param);
    mutation.mutateAsync(param);
  };

  // FILTER

  const setList = (data: CategoryContent[]) => {
    const state = useAnaliseCoberturaStore.getState();
    const list = data?.map(item => ({
      value: item.categoria,
      label: item.categoria,
    }));

    const prev = state.category;
    const cacheList = prev.cache;
    const removeDuplicate = list.filter(i => !prev.value?.includes(i.value));
    const concatList = prev.value?.length ? cacheList.concat(removeDuplicate) : list;
    setCoverageAnalysisState('category', { ...prev, list: concatList });
  };

  const handleChangeCategoryCoverageSearch = async (value: string) => {
    const param = { ...categoryCoverageParams, filtro: [], query: value, page: 0, size: 50 };
    debounce(async () => {
      const resp = await service.getCoverageAnalysisCategory(param);

      if (resp?.status === 200) {
        setList(resp?.data.content);
      }
    });
  };

  const addSecao = (item: Filter) => {
    const param = categoryCoverageParams;
    const prev = category;

    if (!prev.value.includes(item.value)) {
      const value = [...prev.value, item.value];
      const cache = [...(prev.cache || []), item];
      setCoverageAnalysisState('category', { ...prev, value, cache });
      setCoverageAnalysisState('categoryCoverageParams', { ...param, filtro: value });
    }
  };

  const removeSecao = (item: Filter) => {
    const param = categoryCoverageParams;
    const prev = category;

    const value = prev.value.filter(value => value !== item.value);
    const cache = prev.cache?.filter(cache => cache.value !== item.value);
    setCoverageAnalysisState('category', { ...prev, value, cache });
    setCoverageAnalysisState('categoryCoverageParams', { ...param, filtro: value });
  };

  const handleSelectCategoryCoverageFilter = (item: Filter) => {
    const prev = category;
    if (prev.value.includes(item.value)) {
      removeSecao(item);
    } else {
      addSecao(item);
    }
  };

  const handleCleanCategoryCoverageFilter = () => {
    const initialState = coverageInitialState.categoryCoverageParams;
    const param = categoryCoverageParams;
    const prev = category;
    setCoverageAnalysisState('category', { ...prev, value: [], cache: [] });
    setCoverageAnalysisState('categoryCoverageParams', { ...param, filtro: [] });
    mutation.mutateAsync({ ...initialState, diasAnalise: param.diasAnalise, filtro: [] });
  };

  useEffect(() => {
    mutation.mutateAsync(categoryCoverageParams);
  }, []);

  return {
    CategoryCoverageLoading: mutation.isPending,
    handleChangeCategoryCoveragePeriod,
    handleChangeCategoryCoverageSearch,
    handleChangeCategoryCoveragePagination,
    handleSelectCategoryCoverageFilter,
    handleCleanCategoryCoverageFilter,
    handleRequestCategoryCoverageTableList: mutation.mutateAsync,
  };
};
